import { create } from 'zustand';
import type {
  FacialRecognitionData,
  RegistrationData,
} from '@/models/registration-data.model';
import { authValidationService } from '@/services/auth/auth-validation.service';
import { ROLE_OPTIONS } from '@/models/role-option.model';
import { useDataStore } from '@/stores/data.store';
import { useLocalAuthStore } from '@/stores/local-auth.store';

// Re-export for backward compatibility (mirrors Angular re-exports).
export type { RegistrationData, FacialRecognitionData } from '@/models/registration-data.model';
export { TITLE_OPTIONS } from '@/models/registration-data.model';

/**
 * @fileoverview AIEMD Auth Store (Zustand)
 * @description
 * React/Zustand port of the Angular `AiemdAuthService`. Reproduces the public
 * method/state surface 1:1.
 *
 * Adaptation notes (React conversion rules — stores never call a router):
 * - `navigateToStep`, `proceedToFacialRecognition`, `proceedToRoleSelection`,
 *   and `redirectToNextStep` set `currentStep` and/or RETURN the target step
 *   string. Components are responsible for the actual navigation.
 * - The decision logic in `getNextStep()` / `canAccessRoute()` is preserved
 *   line-for-line — guards depend on it.
 * - `FacialRecognitionService` and `InstitutionsService` are not ported yet;
 *   calls to them are stubbed with TODOs. They affect only the deprecated
 *   facial-recognition helpers and institution enrichment in loadUserProfile.
 */

interface AuthState {
  // Reactive state (was Angular signals)
  registrationData: Partial<RegistrationData>;
  facialRecognitionData: FacialRecognitionData;
  isLoading: boolean;
  isLoggingOut: boolean;
  currentStep: string;
  isLoggedIn: boolean;
  currentUser: Partial<RegistrationData> | null;
  isLoadingProfile: boolean;

  updateRegistrationData: (data: Partial<RegistrationData>) => void;
  generateUsername: (
    firstName: string,
    lastName: string,
    title?: string,
    suffix?: string,
  ) => string;
  validateNPI: (npi: string) => { isValid: boolean; message?: string };
  validateDEA: (dea: string) => { isValid: boolean; message?: string };
  validateEmail: (email: string) => { isValid: boolean; message?: string };
  validatePassword: (password: string) => { isValid: boolean; message?: string };
  completeRegistration: (formData?: any) => Promise<boolean>;
  setCurrentStep: (step: string) => void;
  /** Sets currentStep and returns the route; component performs navigation. */
  navigateToStep: (step: string) => string;
  /** Sets currentStep to 'facial-recognition' and returns it; component navigates. */
  proceedToFacialRecognition: () => string;
  skipFacialRecognition: () => Promise<string>;
  /** Sets currentStep to 'role-selection' and returns it; component navigates. */
  proceedToRoleSelection: () => string;
  completeFacialRecognition: (photos: Partial<FacialRecognitionData>) => Promise<string>;
  completeRoleSelection: (role: string, specialty?: string) => Promise<string>;
  getNextStep: () => string;
  /** Reloads profile, computes next step, sets currentStep, returns it; component navigates. */
  redirectToNextStep: () => Promise<string>;
  login: (
    credentials: { email: string; password: string } | { username: string; password: string },
  ) => Promise<{ success: boolean; error?: string }>;
  getGreeting: () => string;
  getFullName: () => string;
  getWelcomeMessage: () => string;
  getWelcomeBackMessage: () => string;
  isRegistrationComplete: () => boolean;
  clearRegistrationData: () => void;
  logout: () => Promise<void>;
  autoLogin: () => void;
  canAccessRoute: (route: string) => boolean;
  getRoleOptions: (primaryOnly?: boolean) => typeof ROLE_OPTIONS;

  // Internal (was private)
  loadUserProfile: () => Promise<void>;
  loadFacialRecognitionData: (userId: string) => Promise<void>;
}

const data = () => useDataStore.getState();
const localAuth = () => useLocalAuthStore.getState();

export const useAuthStore = create<AuthState>((set, get) => ({
  registrationData: {},
  facialRecognitionData: { setupCompleted: false },
  isLoading: false,
  isLoggingOut: false,
  currentStep: 'registration',
  isLoggedIn: false,
  currentUser: null,
  isLoadingProfile: false,

  updateRegistrationData: (patch) => {
    const current = get().registrationData;
    const updated = {
      ...current,
      ...patch,
      lastUpdated: new Date(),
    };
    set({ registrationData: updated });
    // Note: User data updates are now handled by local data.
  },

  generateUsername: (firstName, lastName, title, suffix) =>
    authValidationService.generateUsername(firstName, lastName, title, suffix),

  validateNPI: (npi) => authValidationService.validateNPI(npi),
  validateDEA: (dea) => authValidationService.validateDEA(dea),
  validateEmail: (email) => authValidationService.validateEmail(email),
  validatePassword: (password) => authValidationService.validatePassword(password),

  /** @deprecated Use useLocalAuthStore.signUp() directly instead. */
  completeRegistration: async (formData) => {
    console.warn(
      'AuthStore.completeRegistration() is deprecated. Use useLocalAuthStore.signUp() directly.',
    );

    const d = formData || get().registrationData;

    if (!d.firstName || !d.lastName || !d.email || !d.password || !d.username) {
      throw new Error('Missing required registration fields');
    }

    set({ isLoading: true });

    try {
      const result = await localAuth().signUp({
        email: d.email,
        password: d.password,
        firstName: d.firstName,
        lastName: d.lastName,
        username: d.username,
        institution_id: d.institution?.id || undefined,
        role: d.role || '',
        specialty: d.specialty || undefined,
        npi: d.npi || undefined,
      });

      if (!result.success) {
        console.error('Registration failed:', result.error);
        set({ isLoading: false });
        return false;
      }

      const completeData: RegistrationData = {
        institution: d.institution || null,
        customInstitution: d.customInstitution || '',
        firstName: d.firstName,
        lastName: d.lastName,
        suffix: d.suffix || '',
        title: d.title || '',
        npi: d.npi || '',
        dea: d.dea || '',
        username:
          d.username || get().generateUsername(d.firstName, d.lastName, d.title, d.suffix),
        email: d.email,
        password: '', // Never store password
        enableFacialRecognition: d.enableFacialRecognition || false,
        role: d.role || '',
        specialty: d.specialty || '',
        registrationDate: new Date(),
        lastUpdated: new Date(),
      };

      set({ registrationData: completeData, currentUser: completeData, isLoading: false });
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      set({ isLoading: false });
      return false;
    }
  },

  setCurrentStep: (step) => set({ currentStep: step }),

  navigateToStep: (step) => {
    // React: set step and return route; component performs navigation.
    set({ currentStep: step });
    return '/' + step;
  },

  proceedToFacialRecognition: () => get().navigateToStep('facial-recognition'),

  /** @deprecated Use FacialRecognitionService.skipFacialRecognition() directly instead. */
  skipFacialRecognition: async () => {
    console.warn(
      'AuthStore.skipFacialRecognition() is deprecated. Use FacialRecognitionService.skipFacialRecognition() directly.',
    );

    // TODO: FacialRecognitionService not ported yet — call skipFacialRecognition() once available.

    // Reload user profile to get updated facial recognition data
    await get().loadUserProfile();

    return get().navigateToStep('role-selection');
  },

  proceedToRoleSelection: () => get().navigateToStep('role-selection'),

  /** @deprecated Use FacialRecognitionService.completeFacialRecognition() directly instead. */
  completeFacialRecognition: async (photos) => {
    console.warn(
      'AuthStore.completeFacialRecognition() is deprecated. Use FacialRecognitionService.completeFacialRecognition() directly.',
    );

    const facialData: FacialRecognitionData = {
      ...photos,
      setupCompleted: true,
      setupDate: new Date(),
    };

    set({ facialRecognitionData: facialData });

    // TODO: FacialRecognitionService not ported yet — persist via
    // completeFacialRecognition({ front_photo_url, left_side_photo_url, right_side_photo_url })
    // once available. Decision/navigation logic preserved below.

    return get().navigateToStep('role-selection');
  },

  completeRoleSelection: async (role, specialty) => {
    get().updateRegistrationData({ role, specialty });

    // Save role to database
    const userId = data().getCurrentUserId();
    if (userId) {
      try {
        const { error } = await data()
          .client()
          .from('users')
          .update({
            role: role,
            specialty: specialty || null,
          })
          .eq('id', userId);

        if (error) {
          console.error('Error saving role to database:', error);
          // Continue anyway - the role is saved locally
        }
      } catch (error) {
        console.error('Unexpected error saving role:', error);
        // Continue anyway - the role is saved locally
      }
    }

    // New flow order: role-selection -> facial-recognition -> dashboard
    return get().navigateToStep('facial-recognition');
  },

  getNextStep: () => {
    const regData = get().registrationData;
    const facialData = get().facialRecognitionData;
    const isAuthenticated = get().isLoggedIn;

    const hasValidRole = regData.role && regData.role.trim() !== '';

    // New flow order: registration -> role-selection -> facial-recognition -> dashboard

    // If logged in but no role selected yet, go to role selection first
    if (
      isAuthenticated &&
      regData.firstName &&
      regData.lastName &&
      regData.email &&
      !hasValidRole
    ) {
      return 'role-selection';
    }

    // If logged in with role but haven't completed facial recognition, go to facial recognition
    if (
      isAuthenticated &&
      regData.firstName &&
      regData.lastName &&
      regData.email &&
      hasValidRole &&
      !facialData.setupCompleted
    ) {
      return 'facial-recognition';
    }

    // If logged in and all steps complete (reg + role + facial), go to dashboard
    if (
      isAuthenticated &&
      regData.firstName &&
      regData.lastName &&
      regData.email &&
      hasValidRole &&
      facialData.setupCompleted
    ) {
      return 'dashboard';
    }

    // For all other cases (new users, users who need to login, incomplete registration), go to login-register screen
    return 'login';
  },

  redirectToNextStep: async () => {
    // Explicitly reload the profile to get latest data
    await get().loadUserProfile();

    const nextStep = get().getNextStep();
    return get().navigateToStep(nextStep);
  },

  login: async (credentials) => {
    set({ isLoading: true });

    try {
      let email: string;

      // Convert username to email if needed
      if ('username' in credentials) {
        const { data: row, error } = await data()
          .client()
          .from('users')
          .select('email, username')
          .eq('username', credentials.username)
          .single();

        if (error || !row) {
          console.error('Username not found:', credentials.username, 'Error:', error);
          set({ isLoading: false });
          return { success: false, error: 'Invalid username or password' };
        }

        email = row.email;
      } else {
        email = credentials.email;
      }

      // Sign in with local data
      const result = await localAuth().signIn({
        email: email,
        password: credentials.password,
      });

      if (!result.success) {
        set({ isLoading: false });
        return { success: false, error: result.error || 'Invalid credentials' };
      }

      // Verify user exists in public.users table
      const userId = data().getCurrentUserId();
      if (userId) {
        const { data: userData, error: userError } = await data()
          .client()
          .from('users')
          .select('id')
          .eq('id', userId)
          .single();

        if (userError || !userData) {
          console.error('User profile not found in public.users for userId:', userId);
          // Sign out the user since they don't have a valid profile
          await localAuth().signOut();
          set({ isLoading: false });
          return { success: false, error: 'User does not exist. Please register first.' };
        }
      }

      set({ isLoading: false });
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      set({ isLoading: false });
      return { success: false, error: 'An error occurred during login. Please try again.' };
    }
  },

  getGreeting: () => {
    const currentTime = new Date().getHours();

    let greeting = 'Good evening';
    if (currentTime < 12) {
      greeting = 'Good morning';
    } else if (currentTime < 18) {
      greeting = 'Good afternoon';
    }
    return greeting;
  },

  getFullName: () => {
    const d = get().registrationData;

    const title = d.title || '';
    const name = d.lastName || 'User';
    return title ? `${title} ${name}` : name;
  },

  getWelcomeMessage: () => {
    const greeting = get().getGreeting();
    const fullName = get().getFullName();

    return `${greeting}, ${fullName}. How can I assist you today?`;
  },

  getWelcomeBackMessage: () => {
    const d = get().registrationData;
    const title = d.title || '';
    const name = d.firstName || 'User';
    const fullName = title ? `${title} ${name}` : name;

    return `Welcome back, ${fullName}, how may I assist you?`;
  },

  isRegistrationComplete: () => {
    const d = get().registrationData;
    return !!(d.firstName && d.lastName && d.username && d.password && d.role);
  },

  clearRegistrationData: () => {
    set({
      registrationData: {},
      facialRecognitionData: { setupCompleted: false },
      isLoggedIn: false,
      currentUser: null,
    });
    // Local state cleared - local data session handled separately
  },

  logout: async () => {
    // Set loading state FIRST to show overlay
    set({ isLoggingOut: true });

    // Clear all local state immediately to prevent race conditions
    set({
      registrationData: {},
      facialRecognitionData: { setupCompleted: false },
      currentUser: null,
      isLoggedIn: false, // Explicitly set to false to prevent guard race conditions
    });

    await localAuth().signOut();

    // Reset loading state after navigation completes.
    // Small delay to ensure navigation has started.
    setTimeout(() => {
      set({ isLoggingOut: false });
    }, 100);
  },

  autoLogin: () => {
    // Auto-login is now handled by local data session persistence.
    // This method is kept for backward compatibility but does nothing.
  },

  canAccessRoute: (route) => {
    const regData = get().registrationData;
    const facialData = get().facialRecognitionData;
    const isAuthenticated = get().isLoggedIn;

    switch (route) {
      case 'login':
      case 'registration':
        // Public routes - handled by loginGuard
        return true;

      case 'role-selection':
        // Can only access if logged in AND role NOT yet selected
        return isAuthenticated && !regData.role;

      case 'facial-recognition':
        // Can only access if logged in AND role IS selected AND facial recognition NOT yet completed
        return isAuthenticated && !!regData.role && !facialData.setupCompleted;

      case 'dashboard':
        // Dashboard requires authentication AND role selection AND facial recognition completed
        return isAuthenticated && !!regData.role && facialData.setupCompleted;

      default:
        return true;
    }
  },

  getRoleOptions: (primaryOnly = false) => {
    if (primaryOnly) {
      return ROLE_OPTIONS.filter((role) => role.primary);
    }
    return ROLE_OPTIONS;
  },

  loadUserProfile: async () => {
    const userId = data().getCurrentUserId();
    if (!userId) {
      set({ isLoadingProfile: false });
      return;
    }

    try {
      set({ isLoadingProfile: true });

      const { data: row, error } = await data()
        .client()
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error loading user profile:', error);

        // If user profile doesn't exist (PGRST116 error), sign them out
        if (error.code === 'PGRST116') {
          console.warn('User profile not found in database - signing out stale session');
          await localAuth().signOut();
        }

        set({ isLoadingProfile: false });
        return;
      }

      if (row) {
        // TODO: InstitutionsService not ported yet — enrich `institution` from
        // institution_id once available. Until then it stays null.
        const institution = null;

        const userData: Partial<RegistrationData> = {
          firstName: row.first_name,
          lastName: row.last_name,
          email: row.email,
          title: row.title || '',
          suffix: row.suffix || '',
          npi: row.npi || '',
          dea: row.dea || '',
          username: row.username || '',
          role: row.role || '',
          specialty: row.specialty || '',
          institution: institution,
          customInstitution: row.custom_institution || '',
          enableFacialRecognition: row.enable_facial_recognition || false,
          password: '', // Never load password
        };

        set({ currentUser: userData, registrationData: userData });

        // Load facial recognition data if enabled
        await get().loadFacialRecognitionData(userId);
      }
    } catch (error) {
      console.error('Unexpected error loading user profile:', error);
    } finally {
      set({ isLoadingProfile: false });
    }
  },

  loadFacialRecognitionData: async (userId) => {
    try {
      const { data: row, error } = await data()
        .client()
        .from('facial_recognition')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !row) {
        // No facial recognition data - treat as not completed
        set({ facialRecognitionData: { setupCompleted: false } });
        return;
      }

      // Use the setup_completed value from the database
      set({
        facialRecognitionData: {
          setupCompleted: row.setup_completed ?? false,
          setupDate: row.setup_date ? new Date(row.setup_date) : undefined,
          // Note: We don't load the actual face encoding for security
        },
      });
    } catch (error) {
      console.error('Error loading facial recognition data:', error);
      set({ facialRecognitionData: { setupCompleted: false } });
    }
  },
}));

// Sync isLoggedIn with the data store's auth state (was the Angular constructor effect()).
// On change: when authenticated, load the profile; otherwise clear currentUser.
useDataStore.subscribe((state, prev) => {
  if (state.isAuthenticated === prev.isAuthenticated) {
    return;
  }
  const authenticated = state.isAuthenticated;
  useAuthStore.setState({ isLoggedIn: authenticated });
  if (authenticated) {
    void useAuthStore.getState().loadUserProfile();
  } else {
    useAuthStore.setState({ currentUser: null });
  }
});
